import React from 'react';
import { Loader2, AlertTriangle, CheckCircle2, Info } from 'lucide-react';

// Reused across all Settings tabs. Centralizes loading/error/empty/banner UI
// so each tab stays focused on its own data shape.

export function Card({ title, description, children, actions }) {
  return (
    <div className="panel p-6 space-y-4">
      {(title || actions) && (
        <div className="flex items-start justify-between gap-3">
          <div>
            {title && <h3 className="font-display text-lg font-semibold text-white">{title}</h3>}
            {description && <p className="text-sm text-white/55 mt-1">{description}</p>}
          </div>
          {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

export function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      {label && <label className="label block">{label}</label>}
      {children}
      {hint && <div className="text-xs text-white/40">{hint}</div>}
    </div>
  );
}

export function StatusBadge({ status }) {
  const s = String(status || '').toLowerCase();
  let cls = 'pill-cold';
  let text = status || 'Unknown';
  if (['approved', 'verified', 'active', 'connected', 'live'].includes(s)) {
    cls = 'pill-sold';
    text = 'Approved';
  } else if (['pending', 'in_review', 'submitted', 'trialing'].includes(s)) {
    cls = 'pill-warm';
    text = 'Pending';
  } else if (['rejected', 'failed', 'error', 'past_due', 'canceled', 'unpaid'].includes(s)) {
    cls = 'pill-hot';
    text = s === 'failed' ? 'Failed' : s.charAt(0).toUpperCase() + s.slice(1);
  } else if (['not_started', 'unsubmitted', 'unregistered', 'disconnected'].includes(s)) {
    cls = 'pill';
    text = 'Not Started';
    return (
      <span className={`pill bg-white/5 text-white/60 border-white/10`}>{text}</span>
    );
  }
  return <span className={cls}>{text}</span>;
}

export function LoadingPanel({ label = 'Loading…' }) {
  return (
    <div className="panel p-6 flex items-center gap-3 text-white/55 text-sm">
      <Loader2 className="w-4 h-4 animate-spin" /> {label}
    </div>
  );
}

export function EmptyPanel({ icon: Icon = Info, title, hint, action }) {
  return (
    <div className="panel p-8 text-center text-white/60">
      <Icon className="w-8 h-8 mx-auto mb-3 text-white/30" />
      <div className="font-medium">{title}</div>
      {hint && <div className="text-xs text-white/40 mt-1">{hint}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function StubBanner({ name }) {
  return (
    <div className="flex items-start gap-3 px-4 py-3 rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-200 text-sm">
      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
      <div>
        <strong>{name} is in stub mode.</strong> No external credentials are configured —
        events log locally but no real API calls happen. Set the matching env vars
        and redeploy to go live.
      </div>
    </div>
  );
}

export function SaveButton({ onClick, dirty, busy, label = 'Save Changes' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!dirty || busy}
      className="btn-primary inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
      {busy ? 'Saving…' : label}
    </button>
  );
}

// Shallow equality good enough for our settings forms (flat objects of
// primitives + small arrays). Returns true when the two are NOT equal.
export function isDirty(a, b) {
  try { return JSON.stringify(a) !== JSON.stringify(b); } catch { return true; }
}
